import { useEffect, useRef, useState } from 'react'
import { IconWarningTriangle } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Checkbox } from '../../components/ui/Checkbox'
import { Modal } from '../../components/ui/Modal'

export interface BulkAddPricedConfirmModalProps {
  readonly open: boolean
  /** The number of cart-eligible lines about to be added — drives the body
   * copy's "Moving {count} priced part(s)…" text (design L4561-4567). */
  readonly count: number
  /** UID-401 Part 2 (mixed-draft limitation) — when the Checkout/"Generate
   * quote" CTAs are the caller and this draft mixes cart-eligible catalog
   * lines with priced-but-not-cart-eligible custom lines (an auto-priced
   * candidate that's been Configured), `count` above is already the smaller
   * cart-eligible number — but on its own that reads as "the whole request
   * moves." This is the explicit "only N of your parts move" disclosure the
   * ticket requires so the user isn't surprised the RFQ still has lines on
   * it afterward: the exact count of parts staying behind, or `0`/`undefined`
   * for the ORIGINAL "Add N priced to cart" bulk-button flow, where the
   * unaffected remainder was never ambiguous (that button's own label
   * already says "priced to cart", and its body copy's "The rest of the
   * request is unaffected" line already covers it — no additional line
   * needed there, so this renders nothing by default). */
  readonly remainingOnRfqCount?: number
  readonly onCancel: () => void
  /** `hideNext` is `true` only when the "don't show this again" checkbox was
   * checked at confirm time — the caller is responsible for persisting the
   * `ff_hide_bulk_cart_warn` suppression (ADR-0087 D5); this component owns
   * only the checkbox's own UI state. */
  readonly onConfirm: (hideNext: boolean) => void
}

/**
 * UID-263 (ADR-0087 D2=FULL, D4, UX spec §3) — the bulk "Add N priced to
 * cart" button's confirm modal: "Add to cart and remove from this RFQ?" +
 * the design's amber warning-icon chip + a "don't show this again" checkbox,
 * built directly on the shared `Modal.tsx` shell (NOT `ConfirmDialog.tsx` —
 * ADR-0087's em override: `ConfirmDialog` hardcodes a red `variant="danger"`
 * confirm button and a fixed footer with no checkbox slot, but this action
 * is non-destructive/brand-teal in the design source, with a checkbox and an
 * amber icon chip neither of which `ConfirmDialog` can express).
 *
 * Two WCAG 2.2 AA fixes applied per the UX spec §3/§4 (do NOT ship the
 * design's raw literals here):
 *  1. The "don't show this again" checkbox reuses the shared `Checkbox`
 *     primitive (already a native, keyboard-operable `<input
 *     type="checkbox">` behind a custom-painted chip) — its default
 *     unchecked border (`--color-border-strong`, rgba(0,0,0,.15), 1.41:1)
 *     fails 1.4.11's 3:1 non-text-contrast minimum. Scoped via
 *     `.rfq-bulk-confirm-checkbox` (rfq.css) to darken JUST this instance's
 *     border to rgba(0,0,0,.65) (~7:1) — the shared `.checkbox-input` class
 *     every OTHER checkbox in the app uses is left untouched.
 *  2. The warning-icon glyph uses `--color-warning-text` (#9A4C00, 5.65:1)
 *     rather than the design's raw `#B4690E` literal (3.87:1, icon-only-safe
 *     but not text-safe) — reuses an existing token and is safe even if the
 *     glyph is ever paired with text.
 *
 * Gate 5 W1 fix: the "Add to cart" confirm button MUST render
 * `variant="primary"` (`--color-brand-primary` #0F208B, 13.14:1 on white) —
 * NOT `variant="purple"` (`--color-accent-purple` #9747FF, ~4.5:1, right at
 * the AA edge). `.dc.html`'s bulk-confirm "Add to cart" button (L4567,
 * `background:var(--fictiv-teal)`) resolves to the BRAND token, matching the
 * design and the UX spec's own contrast sign-off — the sibling purple
 * "Submit RFQ" CTA elsewhere in this vertical is a DIFFERENT control with a
 * different token; do not "match" this button to it.
 */
export function BulkAddPricedConfirmModal({
  open,
  count,
  remainingOnRfqCount,
  onCancel,
  onConfirm,
}: BulkAddPricedConfirmModalProps) {
  const [hideNext, setHideNext] = useState(false)
  const cancelButtonRef = useRef<HTMLButtonElement>(null)
  const remainingCount = remainingOnRfqCount ?? 0

  /** Gate-5 C5/W4 (UID-401) — which flow opened this modal, derived from the
   * ONE prop only the cart-bound CTAs pass (`remainingOnRfqCount`, see its doc
   * comment). Deliberately derived rather than added as a second prop: a
   * separate `intent` flag could disagree with `remainingOnRfqCount` about
   * which flow is running, and this modal now makes two safety-relevant
   * decisions off it. */
  const isCartBoundFlow = remainingOnRfqCount !== undefined

  /** Gate-5 C5 — will confirming DISCARD the whole request?
   *
   * `runBulkAdd` → `removeLinesFromRfqAndMaybeDiscard` calls `rfqBundle.discard()`
   * when the batch removes every line (ADR-0087 D2's FULL realignment, UID-312 /
   * ADR-0107 D10's abandonment). For a cart-bound CTA with nothing staying
   * behind, that is exactly what happens — the user clicked "Checkout" and the
   * request they were composing is deleted.
   *
   * ADR-0125 D9 justifies reusing the destructive executor on the grounds that
   * routing through this modal makes the discard "EXPLICIT, never silent". The
   * body copy asserted the opposite ("The rest of the request is unaffected"),
   * which in this branch is not merely vague but FALSE — there is no rest. The
   * disclosure D9 depends on now actually exists. */
  const discardsWholeRequest = isCartBoundFlow && remainingCount === 0 && count > 0

  // UX spec §3 — the checkbox default is NOT sticky across opens: it resets
  // to unchecked every time the modal opens, even right after a prior
  // confirm that had it checked (checking it only suppresses FUTURE opens;
  // once suppressed, this modal never opens again to show a stale state).
  useEffect(() => {
    if (open) setHideNext(false)
  }, [open])

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title="Add to cart and remove from this RFQ?"
      maxWidth={440}
      className="rfq-bulk-confirm-modal"
      initialFocusRef={cancelButtonRef}
      headerContent={
        <div className="rfq-bulk-confirm-header">
          <span className="rfq-bulk-confirm-icon-chip" aria-hidden="true">
            <IconWarningTriangle size={20} />
          </span>
          <h2 className="modal-title">Add to cart and remove from this RFQ?</h2>
        </div>
      }
      footer={
        <>
          <Button ref={cancelButtonRef} variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onConfirm(hideNext)}>
            Add to cart
          </Button>
        </>
      }
    >
      <p className="rfq-bulk-confirm-body">
        Moving{' '}
        <strong>
          {count} priced part{count === 1 ? '' : 's'}
        </strong>{' '}
        to your cart will remove them from this RFQ request.
        {/* Gate-5 C5 — "The rest of the request is unaffected" is only TRUE
         * when something is actually left. In the discard branch there is no
         * rest, so the sentence is replaced rather than merely softened. */}
        {discardsWholeRequest ? '' : ' The rest of the request is unaffected.'}
      </p>
      {/* Gate-5 C5 — the discard disclosure ADR-0125 D9's ruling depends on.
       * Reuses the same warning styling as the header chip so it reads as a
       * consequence, not a footnote. */}
      {discardsWholeRequest && (
        <p className="rfq-bulk-confirm-body rfq-bulk-confirm-discard">
          That is <strong>every part</strong> on this request, so{' '}
          <strong>this RFQ request will be discarded</strong> and will not be submitted. Your parts
          stay in your cart.
        </p>
      )}
      {/* UID-401 Part 2 — only rendered by the Checkout/"Generate quote" CTAs'
       * mixed-draft case (see the prop's doc comment). Explicitly names the
       * count staying behind so "only N move" is never left implicit. */}
      {remainingCount > 0 && (
        <p className="rfq-bulk-confirm-body rfq-bulk-confirm-remaining">
          Only the {count} cart-eligible part{count === 1 ? '' : 's'} above will move — your other{' '}
          <strong>
            {remainingCount} part{remainingCount === 1 ? '' : 's'}
          </strong>{' '}
          will stay on this RFQ and still need a separate quote.
        </p>
      )}
      {/* Gate-5 W4 (UID-401) — the suppression checkbox belongs ONLY to the
       * original "Add N priced to cart" flow (ADR-0087 D5 granted
       * `ff_hide_bulk_cart_warn` to that button, whose own label already says
       * what it does).
       *
       * It must NOT render for the Checkout / "Generate quote" CTAs, which
       * ADR-0125 D9 rules are never skippable. Ticking it there suppressed the
       * warning for a DIFFERENT flow — the plain bulk-add — which also
       * discards drafts: a control in one dialog silently disarming a safety
       * prompt the user never sees again elsewhere.
       *
       * Belt-and-braces with `RfqReviewPage.handleBulkConfirm`, which also
       * only calls `suppressBulkCartWarn()` for the `'plain'` intent. Either
       * guard alone closes the leak; both are kept because the failure is
       * silent and only shows up much later, in another flow. */}
      {!isCartBoundFlow && (
        <Checkbox
          checked={hideNext}
          onChange={setHideNext}
          label="Don't show this again"
          className="rfq-bulk-confirm-checkbox"
        />
      )}
    </Modal>
  )
}
